<script lang="ts">
  import { page, navigating } from '$app/stores';
  import IconChevronDown from '~icons/tabler/chevron-down';
  import AuctionCard from './AuctionCard.svelte';
  import type { AuctionsPageData } from './Auctions';
  import Pagination from '$lib/client/Pagination.svelte';
  import { goto } from '$app/navigation';
  import Dropdown from '$lib/client/Dropdown.svelte';
  import CategoriesMenu from '../CategoriesMenu.svelte';
  import Loader from '$lib/client/Loader.svelte';
  import { onDestroy, onMount } from 'svelte';
  import { browser } from '$app/environment';
  import { createWebsocketConnection } from '$lib/client/helpers/websocket';
  import type { AuctionBid, Currency } from '@prisma/client';
  import type { LiveBidsIndicatorStatus } from '$lib/client/auction/LiveBidsIndicator.svelte';
  import LiveBidsIndicator from '$lib/client/auction/LiveBidsIndicator.svelte';
  import AddKeywordToWatchlist from './AddKeywordToWatchlist.svelte';
  import dayjs from 'dayjs';

  const AUCTION_REMOVE_THRESHOLD = 5; // seconds

  export let nextPage: boolean;
  export let auctions: AuctionsPageData['auctions'];
  export let featuredAuctions: AuctionsPageData['auctions'] | null = null;
  export let categories: AuctionsPageData['categories'];
  export let liveBidsEnabled: boolean = false;

  let searchCategoryId: number | 'all';
  let wsConnectionStatus: LiveBidsIndicatorStatus = 'connecting';
  let ws: Awaited<ReturnType<typeof createWebsocketConnection>>;
  let timer: number | null = null;

  $: isLoggedIn = !!$page.data.authUser;
  $: isLiveBidsEnabled = isLoggedIn && liveBidsEnabled && auctions.length > 0;
  $: searchTerm = $page.url.searchParams.get('q') ?? '';
  $: statusFilter = $page.url.searchParams.get('status') || 'active';
  $: sortBy = $page.url.searchParams.get('sortBy') || 'endsAt';
  $: categoryId = $page.data.currentCategory?.id;
  $: curAuctions = auctions;
  $: isFirstPage = !!$page.url.searchParams.get('page') === false;

  function onSortByChange(event: Event) {
    const value = (event.target as HTMLSelectElement).value;
    const params = new URLSearchParams($page.url.search);

    sortBy = value;
    params.set('sortBy', sortBy);

    // when chaning sorting always should go back to 1st page
    params.delete('page');

    goto(`${$page.url.pathname}?${params.toString()}`);
  }

  function setStatus(value: typeof statusFilter) {
    statusFilter = value;
    if (statusFilter === 'closed') {
      sortBy = 'endsAt';
    } else {
      sortBy = 'createdAt';
    }

    const params = new URLSearchParams($page.url.search);

    // when chaning status always should go back to 1st page
    params.delete('page');

    if (statusFilter === 'active') {
      goto($page.url.pathname);
    } else if (statusFilter === 'closed') {
      params.set('status', 'closed');
      params.set('sortBy', sortBy);
      goto(`${$page.url.pathname}?${params.toString()}`);
    }
  }

  async function handleSearch() {
    let newParams = new URLSearchParams();
    let newPath;

    // do not reset status when searching
    if (statusFilter !== 'active') {
      newParams.set('status', statusFilter);
    }

    if (searchCategoryId === 'all') {
      newPath = `/`;
    } else {
      const category = categories.find((cat) => cat.id === searchCategoryId);

      // if one of the "parent" categories that don't have sub-categories
      if (category) {
        newPath = `/auctions/${category.slug}`;
      }
      // if one of the "sub-categories"
      else {
        const flatCategories = categories.flatMap((cat) => cat.children);
        const subCategory = flatCategories.flat(2).find((cat) => cat.id === searchCategoryId);
        const parentCategory = categories.find((cat) => cat.id === subCategory?.parentId);
        if (subCategory && parentCategory) {
          newPath = `/auctions/${parentCategory.slug}/${subCategory.slug}`;
        } else {
          throw new Error('Sub-category or parent category was not found');
        }
      }
    }

    if (searchTerm) {
      newParams.set('q', searchTerm);
    } else {
      newParams.delete('q');
    }

    const paramsStr = newParams.toString();
    await goto(`${newPath}${paramsStr.length > 0 ? '?' + paramsStr : ''}`);
  }

  onMount(async () => {
    if (browser && isLiveBidsEnabled) {
      ws = await createWebsocketConnection(
        {
          onConnect: () => {
            wsConnectionStatus = 'online';
          },
          onDisconnect: () => {
            wsConnectionStatus = 'connecting';
          },
          onOffline: () => (wsConnectionStatus = 'offline'),
          onMessage: (event) => {
            const { msg, payload } = JSON.parse(event.data);
            if (msg === 'NEW_BID') {
              const { auctionId, bids, newEndsAt } = payload as {
                auctionId: number;
                bids: (AuctionBid & { currency: Currency })[];

                newEndsAt: string;
              };

              const aucIdx = curAuctions.findIndex((auc) => auc.id === auctionId);

              if (aucIdx > -1) {
                curAuctions[aucIdx] = {
                  ...curAuctions[aucIdx],
                  bids,
                  endsAt: new Date(newEndsAt)
                };

                curAuctions = curAuctions.slice().sort((a, b) => a.endsAt.getTime() - b.endsAt.getTime());
              }
            }
          }
        },
        { maxRetries: 4 }
      );

      const startTimer = () => {
        timer = window.setInterval(() => {
          const newAuctions = curAuctions.filter((auc) => {
            const endsAt = auc.endsAt;
            const gapFromNow = dayjs().diff(dayjs(endsAt), 'second', true);
            if (gapFromNow >= AUCTION_REMOVE_THRESHOLD) {
              return false;
            }

            return true;
          });

          curAuctions = newAuctions;
        }, 1000);
      };

      startTimer();
    }
  });

  onDestroy(() => {
    if (ws) {
      ws.close();
    }

    if (timer) {
      window.clearInterval(timer);
    }
  });
</script>

<div class="row">
  <div class="col-auto col-xl-4 d-none d-md-block">
    <CategoriesMenu />
  </div>

  <div class="col">
    <form class="mb-4" on:submit|preventDefault={handleSearch}>
      <div class="input-group mb-3">
        <input
          value={searchTerm}
          on:change={(event) => (searchTerm = event.currentTarget.value)}
          type="text"
          class="form-control"
        />
        <select
          bind:value={searchCategoryId}
          class="search-select form-select bg-light-subtle"
          id="categoryId"
          name="categoryId"
        >
          <option selected={!categoryId} value="all">All Categories</option>
          {#each categories as cat}
            {#if cat.children.length > 0}
              <optgroup label={cat.title}>
                {#each cat.children as subcat}
                  <option value={subcat.id} selected={categoryId === subcat.id}>
                    {subcat.title}
                  </option>
                {/each}
              </optgroup>
            {:else}
              <option value={cat.id} selected={categoryId === cat.id}>{cat.title}</option>
            {/if}
          {/each}
        </select>
        <button class="btn btn-primary" type="submit">Search</button>
      </div>

      {#if searchTerm}
        <div class="badge rounded-pill text-bg-light">
          {searchTerm.toLowerCase()}

          <AddKeywordToWatchlist isSubscribed={$page.data.isSubscribedToKeyword} keyword={searchTerm} />
        </div>
      {/if}
    </form>

    {#if $navigating}
      <div class="d-flex justify-content-center">
        <Loader />
      </div>
    {:else}
      {#if $page.data.currentCategory}
        <h1 class="h5 mb-3">{$page.data.currentCategory.title}</h1>
      {/if}

      {#if featuredAuctions && featuredAuctions.length > 0}
        <div class="bg-light-subtle p-4 rounded mb-5">
          <p class="fs-7 fw-medium mb-3">Featured</p>
          <div class="d-flex flex-column gap-4">
            {#each featuredAuctions as auction}
              {@const highestBid = auction.bids[0]}

              <AuctionCard
                id={auction.id}
                priceType={auction.priceType}
                priceProposals={auction.priceProposals}
                isFavorite={auction.isFavorite}
                title={auction.title}
                category={auction.category.title}
                quantity={auction.quantity}
                endsAt={auction.endsAt}
                ended={auction.hasEnded}
                lastBid={highestBid?.amount}
                currencyId={auction.currencyId}
                startingPrice={auction.startingPrice}
                bidsCount={auction.bids.length}
              />
            {/each}
          </div>
        </div>
      {/if}

      <div class="d-flex flex-wrap align-items-center mb-4 gap-3 justify-content-between">
        <Dropdown>
          <button slot="target" class="d-flex align-items-center gap-1 fs-8 text-secondary">
            Status: {#if statusFilter === 'active'} Active {:else if statusFilter === 'closed'} Closed {/if}
            <IconChevronDown style="margin-top: 2px;" />
          </button>
          <div slot="content">
            <li>
              <button
                class="dropdown-item"
                class:active={statusFilter === 'active'}
                on:click={() => setStatus('active')}
              >
                Active
              </button>
            </li>
            <li>
              <button
                class="dropdown-item"
                class:active={statusFilter === 'closed'}
                on:click={() => setStatus('closed')}
              >
                Closed
              </button>
            </li>
          </div>
        </Dropdown>

        {#if isLoggedIn}
          <div class="d-flex align-items-center gap-2 m-0 ms-md-auto">
            <label for="sortBy" class="w-50 fs-8 text-secondary">Sort By:</label>
            <select
              value={sortBy}
              id="sortBy"
              name="sortBy"
              class="form-select form-select-sm fs-8"
              on:change={onSortByChange}
              style="min-width: 122px;"
            >
              <option value="createdAt">Newest</option>
              <option value="endsAt">
                {#if statusFilter === 'active'}
                  Closing Soon
                {:else if statusFilter === 'closed'}
                  Recently Closed
                {/if}
              </option>
            </select>
          </div>
        {/if}

        {#if isLiveBidsEnabled}
          <LiveBidsIndicator status={wsConnectionStatus} />
        {/if}
      </div>

      {#if curAuctions.length > 0}
        <div class="d-flex flex-column gap-4">
          {#each curAuctions as auction, i}
            {@const highestBid = auction.bids[0]}

            {#if !isLoggedIn && i === 1}
              <a href="/auth/register">
                <img src="/images/banner.jpeg" width="856" height="124" alt="" style="width: 100%; height: auto;" />
              </a>
            {/if}

            <AuctionCard
              id={auction.id}
              priceType={auction.priceType}
              priceProposals={auction.priceProposals}
              isFavorite={auction.isFavorite}
              title={auction.title}
              category={auction.category.title}
              quantity={auction.quantity}
              endsAt={auction.endsAt}
              ended={auction.hasEnded}
              lastBid={highestBid?.amount}
              currencyId={auction.currencyId}
              startingPrice={auction.startingPrice}
              bidsCount={auction.bids.length}
              blurPrice={!isLoggedIn && (isFirstPage ? i >= 10 : true)}
            />
          {/each}
        </div>

        {#if curAuctions.length > 0}
          <Pagination className="mt-5" {nextPage} />
        {/if}
      {/if}

      {#if curAuctions.length === 0 && (featuredAuctions?.length ?? 0) === 0}
        <div class="alert alert-light" role="alert">No listings found</div>
      {/if}
    {/if}
  </div>
</div>

<style>
  .search-select {
    font-size: 0.875em;
    max-width: 160px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  @media (max-width: 767px) {
    .search-select {
      font-size: 0.7em;
      max-width: 125px;
    }
  }
</style>
